<script lang="ts">
  import { onMount } from 'svelte';
  import { BookingModal } from 'customer-components/lib/v2/booking';
  import { fly } from 'svelte/transition';
  import { quintOut } from 'svelte/easing';
  import { CalendarIcon, MessageIcon, PhoneAltIcon } from '$icons';
  import { page } from '$app/stores';
  import type {
    GlobalProperties,
    UseFormattedProps,
  } from 'customer-components/lib/types';
  import { getHref } from 'customer-components/lib/helpers/sections-helper';

  export let properties: UseFormattedProps;
  export let settings: GlobalProperties;
  export let isMarketing = false;

  let isModalActive = false;
  let show = false;

  $: ({ generalSettings, brand, borderRadius } = settings);
  $: phonePriority = settings.generalSettings?.contacts?.phone?.enabled;
  $: phoneNumber = isMarketing
    ? settings.mktSettings?.phoneNumber.value
    : settings?.generalSettings?.contacts?.phone?.enabled
      ? settings?.generalSettings?.contacts?.phone?.value
      : null;
  $: booking =
    settings.generalSettings?.callToAction?.value === 'Book an Appointment';

  onMount(() => {
    checkHeaderVisibility();
  });

  function checkHeaderVisibility() {
    let myElement = document.getElementsByTagName('header')[0];
    let counter = 0;

    if (myElement || counter > 2) {
      // Element is found, do something with it
      updateVisibility();
    } else {
      // Element is not yet present, check again after a delay
      counter++;
      setTimeout(checkHeaderVisibility, 1);
    }
  }

  const updateVisibility = () => {
    show = !isHeaderVisible();
  };

  const isHeroVisible = () => {
    const hero = document.getElementById('hero');
    if (!hero) return false;
    const rect = hero.getBoundingClientRect();
    return rect.bottom >= 0;
  };

  const isHeaderVisible = () => {
    const header = document.getElementsByTagName('header')[0];
    if (!header) return false;
    const rect = header.getBoundingClientRect();
    return rect.bottom >= 0;
  };

  function sendEvent(eventName) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: eventName,
        lpKey: $page.data.businessName,
      });
    } catch (err) {
      console.log(err);
    }
  }

  const handleCTA = () => {
    sendEvent('stickyButtonFormClick');
    if (isMarketing) return (window.location.href = '#form');
    if (
      settings.generalSettings?.contacts?.externalUrl?.enabled &&
      settings?.generalSettings?.contacts?.externalUrl?.value
    ) {
      let url = settings.generalSettings.contacts.externalUrl.value;
      if (!url.includes('http')) url = 'https://' + url;
      window.location.href = url;
      return;
    }

    isModalActive = true;
  };
</script>

<svelte:window on:scroll={updateVisibility} />

<div
  class="mobile-controls"
  class:is-primary={phonePriority}
  class:show
  role="button"
  tabindex="-1"
  transition:fly={{ duration: 400, y: 56, opacity: 1, easing: quintOut }}
>
  {#if phoneNumber}
    <a
      href={getHref('Phone', phoneNumber)}
      class="cta-button"
      style={isMarketing
        ? 'background:var(--color-primary);color:var(--color-neutral-white);font-family:var(--font-family-intertight)'
        : ''}
      on:click={() => sendEvent('stickyButtonFormClick')}
    >
      <PhoneAltIcon />
      {isMarketing ? phoneNumber : 'Call now'}
    </a>
  {/if}
  <button
    class="cta-button"
    style={isMarketing
      ? 'background:var(--color-primary);color:var(--color-neutral-white);font-family:var(--font-family-intertight)'
      : ''}
    on:click={handleCTA}
  >
    {#if booking}
      <CalendarIcon />
      Book now
    {:else}
      <MessageIcon />
      Message now
    {/if}
  </button>
</div>

{#if isModalActive}
  <BookingModal
    bind:isActive={isModalActive}
    {properties}
    {generalSettings}
    {brand}
    {borderRadius}
    hasCalendar={booking}
    isCTAMobile={true}
  />
{/if}

<style>
  .mobile-controls {
    position: fixed;
    bottom: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: auto;
    border: none;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.14px;
    z-index: 21;
    transform: translateY(80px);
    transition: 0.3s ease-in-out;
  }
  .mobile-controls.show {
    transform: translateY(0);
  }

  .cta-button {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    border: none;
    background: var(--vlt-lp-primary);
    color: var(--vlt-lp-primary-text);
    text-decoration: none;
    grid-gap: 4px;
    font-weight: 600;
    padding: 10px 0;
    padding-bottom: 20px;
  }

  .cta-button:not(:last-child) {
    border-right: 1px solid var(--neutral-grey-90);
  }

  @media screen and (min-width: 768px) {
    .mobile-controls {
      display: none;
    }
  }
</style>
